import { LoadingButton } from "@mui/lab";
import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { NavbarIcon } from "../../atoms/navbar/Icon";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { SmallModal } from "../../components/SmallModal";
import { openSnackbar } from "../../context/SnackbarContext";
import { useOrganizationVariable } from "../../hooks/useOrganizationVariables";
import {
  addOrganizationVariable,
  deleteOrganizationVariable,
  editOrganizationVariable,
} from "../../services/VariablesService";
import { CustomAxiosError } from "../../utilities/ErrorResponseHelper";

type OrganizationVariableDetailsModalProps = {
  /** The associated metric result */
  objectId: string | null;
  /** Should the modal be open? */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
};

export const OrganizationVariableDetailsModal = (props: OrganizationVariableDetailsModalProps) => {
  const { open, onClose, objectId } = props;

  const theme = useTheme();
  const { data: variable, isLoading } = useOrganizationVariable(objectId ?? "", {});
  const [name, setName] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [valueError, setValueError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  useEffect(() => {
    if (variable) {
      setName(variable.name);
      setValue(variable.value);
    } else {
      setName("");
      setValue("");
    }
  }, [variable]);

  const handleSave = async () => {
    try {
      setLoading(true);
      let validationError = false;
      if (name === "") {
        setNameError("Name is a required field");
        validationError = true;
      } else {
        setNameError("");
      }
      if (value === "") {
        setValueError("Value is a required field");
        validationError = true;
      } else {
        setValueError("");
      }
      if (validationError) {
        return;
      }
      if (variable) {
        await editOrganizationVariable(variable.id, {
          name,
          value,
        });
      } else {
        await addOrganizationVariable({
          name,
          value,
        });
      }
      openSnackbar(
        `Environment variable successfully ${variable ? "updated" : "created"}`,
        "success"
      );
      onClose();
      setName("");
      setValue("");
    } catch (error) {
      if (error instanceof CustomAxiosError) {
        error.showAuditLogSnackbar("Failed to update environment variable");
      } else {
        openSnackbar("Failed to update environment variable", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SmallModal
      position={"right"}
      size={"small"}
      open={open}
      onClose={() => onClose()}
      showCloseButton
      isLoading={isLoading}
      title={`${variable ? `Edit` : `Create`} Variable`}
      rightComponent={
        <Box
          display="flex"
          alignItems="center"
          sx={{
            cursor: "pointer",
          }}
          onClick={() => setOpenDelete(true)}
        >
          <NavbarIcon
            variant="trash-can"
            color="error"
            sx={{
              minWidth: "16px",
              width: "16px",
              height: "16px",
              minHeight: "16px",
            }}
          />
        </Box>
      }
      action={
        <LoadingButton loading={loading} variant="contained" fullWidth onClick={() => handleSave()}>
          {variable ? "Save" : "Create"}
        </LoadingButton>
      }
    >
      <Stack gap="10px">
        {variable && (
          <ConfirmationModal
            open={openDelete}
            onClose={() => setOpenDelete(false)}
            title="Delete Variable"
            description="Are you sure you want to delete the variable?
                     Once it’s deleted it’s gone for good"
            acceptText="Yes, delete variable"
            onAccept={async () => {
              deleteOrganizationVariable(variable.id);
              onClose();
            }}
          />
        )}
        <Typography variant="h3">Name</Typography>
        <TextField
          error={nameError !== ""}
          helperText={nameError}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          label="Name"
        />
        <Typography variant="h3">Value</Typography>
        <TextField
          error={valueError !== ""}
          helperText={valueError}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          label="The environment variable value"
          multiline
          minRows={5}
          maxRows={5}
        />
      </Stack>
    </SmallModal>
  );
};
